import { apiClient } from '@indipia/shared'
import type { ApiResponse } from '@indipia/shared-types'
import type { CustReqDelivered, CustReqReportItem } from '../types/custReqProgress.types'

// 공종 상세창 시공 진행 내역 조회(B-05·B-07) — 52-Planner TaskProgressController.
//  GET /schedule/tasks/{taskId}/progress-summary : 요약(작업요청 건수·최근 발송 시각, 작업보고 날짜별 상태·검수·이슈)
//  GET /schedule/tasks/{taskId}/progress-detail  : 세부(작업요청 n건 본문·수신자·사진 + 작업보고 날짜별 내용)
// 요청 헤더(X-Comp-Id·X-User-Id)는 게이트웨이가 주입한다. 일시는 서버 응답 문자열 그대로 보관한다.

/** 현장보고 상태(공통코드 ZEU_FIELD_REPORT_STATUS) — 판정 불가면 null */
export type ReportStatusCode = 'IN_PROGRESS' | 'COMPLETED' | 'ISSUE'

export interface ProgressReportDay {
  /** YYYY-MM-DD */
  workDate: string
  reportCount: number
  reportStatus: ReportStatusCode | (string & {}) | null
  issueYn: 'Y' | 'N' | (string & {})
  /** Y = 그 날 보고가 전부 검수됨 */
  approvalYn: 'Y' | 'N' | (string & {})
  approvalDtm: string | null
}

export interface TaskProgressSummary {
  taskId: number
  taskStartDate: string | null
  taskEndDate: string | null
  workOrderCount: number
  lastWorkOrderSentDtm: string | null
  reportDayCount: number
  latestWorkDate: string | null
  latestReportStatus: ReportStatusCode | (string & {}) | null
  reportDays: ProgressReportDay[]
}

export interface WorkOrderRecipient { partnerId: number | null; partnerName: string | null; role: 'WORKER' | 'CC' | (string & {}); viewed: boolean }

/** 사진 — url 은 인증이 필요한 게이트웨이 경로(/api/worklog/...). 인디포토 출처만 있는 사진은 url 이 null */
export interface ProgressPhoto { url: string | null; thumbUrl: string | null }

export interface WorkOrderItem {
  workOrderId: number
  workTypeName: string | null
  orderContent: string | null
  recipients: WorkOrderRecipient[]
  photoCount: number
  photos: ProgressPhoto[]
  /** YYYY-MM-DDTHH:mm:ss */
  sentAt: string | null
  /** 전달된 고객 요청(S16, K1 3.9.1) — 응답에 필드가 없으면 undefined */
  customerRequests?: CustReqDelivered[]
}

export interface FieldReportItem {
  fieldReportId: number
  writerName: string | null
  workCmplYn: 'Y' | 'N' | null
  issueYn: 'Y' | 'N' | (string & {})
  issueTypeNames: string[]
  reportContent: string | null
  photoCount: number
  photos: ProgressPhoto[]
  reportDtm: string | null
  approvalYn: 'Y' | 'N' | (string & {}) | null
  approvalDtm: string | null
  /** 고객 요청 반영(S16, K1 3.9.2) — 응답에 필드가 없으면 undefined */
  customerRequests?: CustReqReportItem[]
}

export interface ProgressReportDayDetail { workDate: string; reports: FieldReportItem[] }

export interface TaskProgressDetail {
  taskId: number
  workOrders: WorkOrderItem[]
  reportDays: ProgressReportDayDetail[]
}

function need<T>(v: T | null | undefined, what: string): T {
  // 필수 응답 값이 없으면 기본값으로 채우지 않고 오류
  if (v == null) throw new Error(`${what} 응답이 비어 있음`)
  return v
}

export const taskProgressApi = {
  async getSummary(taskId: number, signal?: AbortSignal): Promise<TaskProgressSummary> {
    const res = await apiClient.get<ApiResponse<TaskProgressSummary>>(`/schedule/tasks/${taskId}/progress-summary`, { signal })
    const d = need(res.data?.data, '시공 진행 요약')
    if (!Array.isArray(d.reportDays)) throw new Error('시공 진행 요약 형식 오류')
    return d
  },

  async getDetail(taskId: number, signal?: AbortSignal): Promise<TaskProgressDetail> {
    const res = await apiClient.get<ApiResponse<TaskProgressDetail>>(`/schedule/tasks/${taskId}/progress-detail`, { signal })
    const d = need(res.data?.data, '시공 진행 세부')
    if (!Array.isArray(d.workOrders) || !Array.isArray(d.reportDays)) throw new Error('시공 진행 세부 형식 오류')
    return d
  },
}
